import React, { Suspense, useContext, useState } from "react";
import FullLoader from "components/loader/full-loader";
import { Dropdown } from "react-bootstrap";
import { useDeleteData, useFetchData } from "hooks/useApiHooks";
import LayoutDashboard from "layouts/layout-dashboard-collapse";
import { AppContext } from "contexts/AppContext";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import CreatePage from "./components/create-page";
import { useDeleteHandler } from "hooks/useDeleteHandler";
import { buildQueryString } from "utils/queryHelpers";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import { LIMIT_DATA_PER_PAGE } from "utils/constants";
import { HR_PERMISSION_PRESETS } from "utils/hrPermissionKeys";

const SearchInput = ({ onSearch, defaultValue }) => {
    const [search, setSearch] = useState(defaultValue || '');

    return (
        <div className="d-flex gap-2">
            <div className="input-icon flex-fill">
                <input
                    type="text"
                    className="form-control"
                    placeholder="Search permission..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') onSearch(e.target.value);
                    }}
                />
                <span className="input-icon-addon">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="icon icon-tabler icon-tabler-search"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <circle cx="10" cy="10" r="7" />
                        <line x1="21" y1="21" x2="15" y2="15" />
                    </svg>
                </span>
            </div>
            <button type="button" className="btn btn-outline-primary" onClick={() => onSearch(search)}>
                Cari
            </button>
        </div>
    )
};

const PermissionsPage = () => {
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const queryString = buildQueryString({ search: search || '', page: currentPage, limit: LIMIT_DATA_PER_PAGE });

    useFetchData(`/permission${queryString}`, 'permissions');
    const { deleteData } = useDeleteData('/permission', 'permissions', queryString);

    const { state } = useContext(AppContext);
    const { permissions } = state;

    const handleDelete = useDeleteHandler(deleteData, `User has been deleted`, `Failed to delete User`);
    const { handleSuccess, handleError, showCanvasHandler } = useCanvasHandler();

    const handleCreate = (e) => {
        e.preventDefault();
        showCanvasHandler('Create Permission',
            <CreatePage
                onSuccess={handleSuccess}
                onError={handleError}
            />
        );
    };

    const handleCreateFromPreset = (e, preset) => {
        e.preventDefault();
        showCanvasHandler('Create Permission',
            <CreatePage
                initialDefaults={preset}
                onSuccess={handleSuccess}
                onError={handleError}
            />
        );
    };

    const handleUpdate = (e, data) => {
        e.preventDefault();
        showCanvasHandler('Update Permission',
            <CreatePage
                initialData={data}
                onSuccess={handleSuccess}
                onError={handleError}
            />
        );
    };

    const handleSearch = (value) => {
        setSearch(value);
        setCurrentPage(1);
    };

    const totalPages = permissions.pagination?.last_page || 1;

    const Pagination = usePaginationWithComponent({
        totalPages,
        currentPage,
        onPageChange: setCurrentPage,
        visiblePages: 5, // Optional: number of visible pages in pagination (default is 5)
    });

    if (permissions.loading) return <FullLoader />;
    if (permissions.error) return <Suspense error={permissions.error} />;

    return (
        <LayoutDashboard>
            <div className="container-fluid mt-5">
                <div className="table-container">
                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                        <div>
                            <h2 className="mb-1">Permissions</h2>
                            <div className="text-muted">
                                Kelola akses fitur sebelum dipasang ke role.
                            </div>
                        </div>
                        <button className="btn btn-primary" onClick={handleCreate}>Create Permission</button>
                    </div>

                    <div className="card mb-3">
                        <div className="card-body py-3">
                            <div className="fw-bold mb-2">Rekomendasi permission HR Asset</div>
                            <div className="row g-2">
                                {HR_PERMISSION_PRESETS.map((preset) => (
                                    <div className="col-md-4" key={preset.name}>
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary w-100 h-100 text-start"
                                            onClick={(e) => handleCreateFromPreset(e, preset)}
                                        >
                                            <div className="fw-bold">{preset.display_name}</div>
                                            <div className="small text-muted">{preset.name}</div>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="search-container mb-3">
                        <SearchInput onSearch={handleSearch} defaultValue={search} />
                    </div>

                    {/* Table */}
                    <div className="table">
                        <table className="table table-hover table-vcenter">
                            <thead>
                                <tr>
                                    <th>No</th>
                                    <th>Display Name</th>
                                    <th>Name</th>
                                    <th>Group</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            {permissions.data.length > 0 && (
                                <tbody>
                                    {permissions.data.map((data, index) => {
                                        return (
                                            <React.Fragment key={index}>
                                                <tr>
                                                    <td>
                                                        {index + 1}
                                                    </td>
                                                    <td>{data.display_name}</td>
                                                    <td>{data.name}</td>
                                                    <td>{data.group}</td>
                                                    <td className="text-end">
                                                        <Dropdown>
                                                            <Dropdown.Toggle variant="default" id="dropdownMenuButton1">
                                                                Actions
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu>
                                                                <Dropdown.Item href="#" onClick={(e) => handleUpdate(e, data)}>Edit</Dropdown.Item>
                                                                <Dropdown.Item href="#" onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDelete(data.id);
                                                                }}>
                                                                    Remove
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </td>
                                                </tr>
                                            </React.Fragment>
                                        )
                                    })}
                                </tbody>
                            )}
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="mt-4">
                        {Pagination}
                    </div>
                </div>
            </div>
        </LayoutDashboard>
    );
};

export default PermissionsPage;
